import { AxiosPromise } from 'axios';

import httpClient from '~api/httpClient';

export enum CallHistoryCustomerPhone {
	Anonymous = 'Anonymous',
	Unknown = 'Unknown',
}

/**
 * Which configured phone number the list is scoped to:
 * MARKETING → businessPhoneNumber, TRANSACTIONAL → transactionalPhoneNumber.
 */
export enum CallHistoryNumberType {
	MARKETING = 'MARKETING',
	TRANSACTIONAL = 'TRANSACTIONAL',
}

/**
 * A single structured utterance from the call's transcript (Retell `transcript_object`).
 * Only returned by the per-call detail endpoint, not the paginated list.
 */
export interface CallTranscriptObjectItem {
	content: string;
	role: 'agent' | 'user' | 'transfer_target';
}

export interface CallHistoryItem {
	id: string;
	businessName: string;
	/** The Assist number that was dialled; null on calls stored before it was recorded. */
	businessPhone: string | null;
	/**
	 * Whether the call came in on the marketing or the transactional number. Null when the dialled
	 * number matched neither of the business's numbers when the call was stored.
	 */
	phoneNumberType: CallHistoryNumberType | null;
	customerName: string;
	customerId: string | null;
	customerPhone: string;
	hoursAndLocations: boolean;
	servicesAndPrices: boolean;
	newOrder: boolean;
	existingOrder: boolean;
	attendent: boolean;
	startDateTime: string;
	callDuration: number;
	callCost: number;
	callSummary: string;
	callType: string;
	transcript: string;
	/**
	 * Structured transcript turns. Present on the per-call detail response; absent on list rows.
	 * Preferred over parsing the `transcript` string. The translated (`englishTranscript`) view has
	 * no structured equivalent and still relies on the string.
	 */
	transcriptObject?: CallTranscriptObjectItem[] | null;
	englishTranscript: string;
	recording_url: string;
	transferCallNumber: string | null;
	call_reason: string;
	transfer_reason: string;
	user_sentiment: string;
}

export const getCallHistoryData = (
	startDate: string,
	endDate: string,
): AxiosPromise<CallHistoryItem> => {
	const url = `/business-owner/call-history/retell/logs?startDate=${startDate}&endDate=${endDate}`;

	return httpClient({
		method: 'GET',
		url,
	});
};

export type CallHistorySortOrder = 'ascending' | 'descending';

export interface PaginatedCallHistoryQueryParams {
	startDate: string;
	endDate: string;
	page?: number;
	pageSize?: number;
	sortOrder?: CallHistorySortOrder;
	/** Which number to scope the list to. Defaults to MARKETING. */
	numberType?: CallHistoryNumberType;
	/**
	 * Free-text search routed server-side: input with letters matches customer name,
	 * digits match customer phone. Empty/omitted means no search filter.
	 */
	q?: string;
}

export interface PaginatedCallHistoryResponse {
	data: CallHistoryItem[];
	total: number;
	page: number;
	pageSize: number;
	hasNext: boolean;
}

export const getPaginatedCallHistoryData = (
	params: PaginatedCallHistoryQueryParams,
): AxiosPromise<PaginatedCallHistoryResponse> => {
	return httpClient({
		method: 'GET',
		url: '/business-owner/call-history/retell/calls',
		params,
	});
};

/**
 * Fetch a single call's full detail. The Retell v3 list-calls response no longer carries the
 * transcript, so the detail view fetches it lazily per call: this endpoint re-adds `transcript`
 * and `transferCallNumber` (via `call.retrieve`) on top of the same shape as a list row.
 */
export const getRetellCallDetail = (callId: string): AxiosPromise<CallHistoryItem> => {
	return httpClient({
		method: 'GET',
		url: `/business-owner/call-history/retell/calls/${encodeURIComponent(callId)}`,
	});
};
